import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input } from 'reactstrap';
import styled from 'styled-components';

const StyledModal = styled(Modal)`
  .modal-content {
    border-radius: 12px;
    border: none;
  }
`;

const StyledModalHeader = styled(ModalHeader)`
  background: white;
  color: #7c3aed;
  border-bottom: none;
  padding: 24px 32px 16px 32px;
  
  .modal-title {
    font-size: 20px;
    font-weight: 600;
    color: #7c3aed;
  }
  
  .close {
    color: #9ca3af;
    opacity: 1;
    text-shadow: none;
    font-size: 28px;
    font-weight: 300;
    padding: 0;
    margin: 0;
    
    &:hover {
      color: #6b7280;
    }
  }
`;

const StyledModalBody = styled(ModalBody)`
  padding: 16px 32px 24px 32px;
`;

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledLabel = styled(Label)`
  font-weight: 600;
  color: #374151;
  font-size: 15px;
  margin-bottom: 0;
  margin-right: 16px;
  min-width: 120px;
  text-align: left;
`;

const StyledInput = styled(Input)`
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #6b7280;
  flex: 1;
  
  &:focus {
    background-color: white;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
    outline: none;
  }
  
  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

const StyledSelect = styled(Input)`
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  flex: 1;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  cursor: pointer;
  
  &:focus {
    background-color: white;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
    outline: none;
  }

  /* Add calendar/clock icon before dropdown arrow */
  &.with-icon {
    background-image: 
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E"),
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E");
    background-position: right 12px center, right 36px center;
    padding-right: 60px;
  }

  &.with-time-icon {
    background-image: 
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E"),
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Ccircle cx='12' cy='12' r='10'%3E%3C/circle%3E%3Cpolyline points='12 6 12 12 16 14'%3E%3C/polyline%3E%3C/svg%3E");
    background-position: right 12px center, right 36px center;
    padding-right: 60px;
  }
`;

const OtherReasonInput = styled(Input)`
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  color: #374151;
  margin-top: 8px;
  
  &:focus {
    background-color: white;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  gap: 12px;
  border-top: none;
`;

const StyledButton = styled(Button)`
  border-radius: 8px;
  padding: 12px 48px;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.2s;
  min-width: 140px;
  
  &.btn-primary {
    background: linear-gradient(135deg, #7c3aed 0%, #6366f1 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(124, 58, 237, 0.4);
      background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%);
    }

    &:active {
      transform: translateY(0);
    }
  }
  
  &.btn-secondary {
    background: white;
    color: #7c3aed;
    border: 2px solid #e5e7eb;
    
    &:hover {
      background: #f9fafb;
      color: #6d28d9;
      border-color: #d1d5db;
    }
  }
`;

const RescheduleAppointmentModal = ({
  modalIsOpen,
  closeModal,
  submitModal,
  appointmentData,
  onComplete,
  availableSlotsData,
  reasonOptions = ['Doctor not available', 'Patient requested', 'Other'],
}) => {
  const [selectedReason, setSelectedReason] = useState('Doctor not available');
  const [otherReasonText, setOtherReasonText] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);

  // console.log("availableSlotsData",availableSlotsData)

  // Helper function to format date for display (e.g., "09 Feb 2026")
  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  useEffect(() => {

  
    if (modalIsOpen && availableSlotsData?.length > 0) {
      // Set default reason
     // initialize reason once
    if (!selectedReason) {
        setSelectedReason(
            appointmentData?.reason || reasonOptions[0]
        );
        setOtherReasonText('');
    }
  

       // initialize date + slots once
    if (!selectedDate) {
        const firstDateObj = availableSlotsData[0];

        setSelectedDate(firstDateObj.date);
        setAvailableSlots(firstDateObj.slots);

        if (firstDateObj.slots.length > 0) {
            setSelectedSlotId(firstDateObj.slots[0].slotId);
        }
    }
    }
  }, [modalIsOpen, availableSlotsData,]);

  // Update available slots when date changes
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    
    // Find slots for selected date
    const dateData = availableSlotsData.find(d => d.date === newDate);
    if (dateData) {
      setAvailableSlots(dateData.slots);
      // Auto-select first slot
      if (dateData.slots.length > 0) {
        setSelectedSlotId(dateData.slots[0].slotId);
      }
    } else {
      setAvailableSlots([]);
      setSelectedSlotId('');
    }
  };

  const handleReschedule = () => {
    if (!selectedReason) {
     toast.error('Please select a reason');
      return;
    }

    if (selectedReason === 'Other' && !otherReasonText.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    if (!selectedSlotId) {
      toast.error('Please select a time slot');
      return;
    }

    // Find selected slot details
    const selectedSlot = availableSlots.find(s => s.slotId === parseInt(selectedSlotId));

    // Prepare data for submission
    const rescheduleData = {
      requestId: appointmentData?.rid,
      reason: selectedReason === 'Other' ? otherReasonText : selectedReason,
      newAppointmentDate: selectedDate,
      newAppointmentSlotId: selectedSlotId,
      newAppointmentSlot: selectedSlot?.slot,
    };

    console.log('Reschedule data:', rescheduleData);

    if (submitModal) {
      submitModal(rescheduleData);
    }
    if (onComplete) {
      onComplete();
    }
    closeModal();
  };

  return (
    <StyledModal isOpen={modalIsOpen} toggle={closeModal} size="md">
      <StyledModalHeader toggle={closeModal}>
        <div className="modal-title">
          Reschedule Appointment
        </div>
      </StyledModalHeader>
      
      <StyledModalBody>
        {/* Read-only Display Fields */}
        <StyledFormGroup>
          <StyledLabel>RID:</StyledLabel>
          <StyledInput
            type="text"
            value={appointmentData?.rid || ''}
            disabled
          />
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Patient Name:</StyledLabel>
          <StyledInput
            type="text"
            value={appointmentData?.patientName || ''}
            disabled
          />
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Doctor:</StyledLabel>
          <StyledInput
            type="text"
            value={appointmentData?.doctor || ''}
            disabled
          />
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Center:</StyledLabel>
          <StyledInput
            type="text"
            value={appointmentData?.center || ''}
            disabled
          />
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Center Address:</StyledLabel>
          <StyledInput
            type="text"
            value={appointmentData?.address || ''}
            disabled
          />
        </StyledFormGroup>

        {/* Editable Fields */}
        <StyledFormGroup style={{ alignItems: 'flex-start' }}>
          <StyledLabel style={{ paddingTop: '10px' }}>Reason:</StyledLabel>
          <div style={{ flex: 1 }}>
            <StyledSelect
              type="select"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              {reasonOptions.map((reason, index) => (
                <option key={index} value={reason}>
                  {reason}
                </option>
              ))}
            </StyledSelect>
            
            {/* Show text input if "Other" is selected */}
            {selectedReason === 'Other' && (
              <OtherReasonInput
                type="text"
                placeholder="Please specify reason..."
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
              />
            )}
          </div>
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Date:</StyledLabel>
          <StyledSelect
            type="select"
            className="with-icon"
            value={selectedDate}
            onChange={handleDateChange}
          >
            {availableSlotsData?.map((dateObj, index) => (
              <option key={index} value={dateObj.date}>
                {formatDateForDisplay(dateObj.date)}
              </option>
            ))}
          </StyledSelect>
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Timing:</StyledLabel>
          <StyledSelect
            type="select"
            className="with-time-icon"
            value={selectedSlotId}
            onChange={(e) => setSelectedSlotId(e.target.value)}
          >
            {availableSlots.length === 0 ? (
              <option value="">No slots available</option>
            ) : (
              availableSlots.map((slot) => (
                <option key={slot.slotId} value={slot.slotId}>
                  {slot.slot}
                </option>
              ))
            )}
          </StyledSelect>
        </StyledFormGroup>
      </StyledModalBody>
      
      <StyledModalFooter>
        <StyledButton color="primary" onClick={handleReschedule}>
          Reschedule
        </StyledButton>
        <StyledButton color="secondary" onClick={closeModal}>
          Close
        </StyledButton>
      </StyledModalFooter>
    </StyledModal>
  );
};

export default RescheduleAppointmentModal;